/**
 * Admin health utilities.
 * Derive operational status and attention items from API data.
 * Pure functions — no side effects, no env access.
 *
 * Attention items are action-oriented: each tells the admin
 * what happened, why it matters, and what to do about it.
 */

/**
 * Attention item severity levels (higher = more critical).
 */
const SEVERITY = {
  error: 3,
  warning: 2,
  info: 1,
}

/**
 * Known all-organic stores where 0% accept rate is expected
 * because their products rarely carry bio markers in their names.
 * These use skipBioCheck: true in the scraper config.
 */
const ALL_ORGANIC_STORES = new Set(['ekoplaza', 'odin'])

/**
 * Derive attention items from the admin overview data.
 * Each item has:
 *   - severity: error | warning | info
 *   - title: what happened (factual)
 *   - detail: why it matters + what to do
 *   - action: verb phrase for the CTA button
 *   - link: where clicking should navigate
 *
 * @param {object} data - The full admin API response
 * @returns {Array<{severity: string, title: string, detail: string, action: string, link: string}>}
 */
export function deriveAttentionItems(data) {
  const items = []

  // --- Sources with errors ---
  const sources = data?.sourceStats?.sources ?? []
  const errorSources = sources.filter(s => s.errors > 0)
  for (const s of errorSources) {
    const errorMsg = s.errorDetail
      ? (typeof s.errorDetail === 'string' ? s.errorDetail : JSON.stringify(s.errorDetail))
      : `${s.errors} error(s) during last scrape`
    items.push({
      severity: 'error',
      title: `${capitalize(s.source)} scraper failed`,
      detail: `${errorMsg}. Check if the store website changed or the URL is still valid.`,
      action: 'Investigate scraper',
      link: '/sources',
    })
  }

  // --- Sources with zero accepted offers ---
  const zeroSources = sources.filter(s => s.accepted === 0 && s.errors === 0)
  for (const s of zeroSources) {
    const isExpected = s.extracted === 0
    const isAllOrganic = ALL_ORGANIC_STORES.has(s.source.toLowerCase())

    if (isExpected) {
      // Extracted 0 — likely the scraper got no data
      items.push({
        severity: 'warning',
        title: `${capitalize(s.source)} returned no products`,
        detail: 'The scraper connected but found nothing. The store may have no current promotions, or the page structure may have changed.',
        action: 'Check scraper',
        link: '/sources',
      })
    } else if (isAllOrganic) {
      // All-organic store with high extraction but 0 acceptance
      // This likely means the skipBioCheck flag is working but the
      // category filter is rejecting everything (not produce)
      items.push({
        severity: 'info',
        title: `${capitalize(s.source)} accepted 0 of ${s.extracted}`,
        detail: `This all-organic store extracted ${s.extracted} products but none were classified as fresh produce. This is normal if the store mainly has non-produce items this week.`,
        action: 'Review offers',
        link: '/offers',
      })
    } else {
      // Regular store: extracted products but none passed the filter
      items.push({
        severity: 'warning',
        title: `${capitalize(s.source)} accepted 0 of ${s.extracted}`,
        detail: `${s.extracted} products extracted but none passed the bio/produce filter. The LLM may be misclassifying products. Review recent classification disagreements.`,
        action: 'Review classifications',
        link: '/triage',
      })
    }
  }

  // --- Failed or partial recent runs ---
  const runs = data?.scrapeRuns ?? []
  const recentRuns = runs.slice(0, 3) // Only flag recent runs
  const failedRuns = recentRuns.filter(r => r.status === 'failed')
  const partialRuns = recentRuns.filter(r => r.status === 'partial')

  for (const r of failedRuns) {
    items.push({
      severity: 'error',
      title: 'Scrape run failed completely',
      detail: `The run on ${formatDateSafe(r.startedAt)} crashed before completing. Check the error logs and re-trigger if it was transient.`,
      action: 'View run details',
      link: '/runs',
    })
  }

  for (const r of partialRuns) {
    items.push({
      severity: 'warning',
      title: `Scrape run partially completed`,
      detail: `${r.errors} source(s) errored on ${formatDateSafe(r.startedAt)}. Offers from working sources were saved, but some stores are missing.`,
      action: 'View run details',
      link: '/runs',
    })
  }

  // --- Classification health ---
  // The golden set is the ONLY quality gate. Agreement rate (LLM vs keyword
  // fallback) is a diagnostic — disagreement is expected and healthy because
  // the LLM correctly handles cases keywords can't (and vice versa).
  // Only surface alerts when the golden set actually fails.
  const classStats = data?.classificationStats
  if (classStats) {
    const goldenSet = classStats.goldenSet

    // Golden set failures — the classifier is genuinely broken
    if (goldenSet && goldenSet.failed > 0) {
      const failedNames = goldenSet.failures?.map(f => f.name).join(', ') || 'unknown'
      items.push({
        severity: 'error',
        title: `Pipeline broken — ${goldenSet.failed} golden set failure(s)`,
        detail: `The classifier misclassified known-good products: ${failedNames}. This means the LLM or prompt is producing wrong results. Investigate the classifier prompt and check if Workers AI is responding correctly.`,
        action: 'Investigate classifier',
        link: '/triage',
      })
    }
    // Golden set not yet run — nudge but don't alarm
    else if (!goldenSet) {
      items.push({
        severity: 'info',
        title: 'Golden set has not run yet',
        detail: 'The golden set validates the classifier against known-good products. It runs automatically during scrapes. No action needed — it will populate on the next run.',
        action: 'View triage',
        link: '/triage',
      })
    }
    // Golden set passes → classifier is working. No action item.
  }

  // --- Failed/pending embeddings ---
  const embeddings = data?.pendingEmbeddings
  if (embeddings) {
    if (embeddings.failed > 0) {
      items.push({
        severity: 'error',
        title: `${embeddings.failed} embedding(s) failed`,
        detail: 'These products could not be embedded into Vectorize. They won\'t appear in cross-store comparisons until re-embedded. This usually fixes itself on the next scrape run.',
        action: 'View clusters',
        link: '/clusters',
      })
    }
    // Pending embeddings are normal during processing — only flag large backlogs
    if (embeddings.pending > 10) {
      items.push({
        severity: 'info',
        title: `${embeddings.pending} embeddings queued`,
        detail: 'Products waiting to be embedded. This backlog should clear within 1-2 scrape runs.',
        action: 'View clusters',
        link: '/clusters',
      })
    }
  }

  // --- Offer health ---
  const offerHealth = data?.offerHealth
  if (offerHealth) {
    if (offerHealth.insufficientUnitData > 0) {
      items.push({
        severity: 'info',
        title: `${offerHealth.insufficientUnitData} offer(s) can't be compared by weight`,
        detail: 'Unit data (e.g. "500g", "1 stuk") isn\'t recognized for these offers. Price-per-kg comparison falls back to raw price. Fix by improving unit parsing in the scraper.',
        action: 'View affected offers',
        link: '/offers',
      })
    }
    if (offerHealth.suspiciousDeals > 0) {
      items.push({
        severity: 'warning',
        title: `${offerHealth.suspiciousDeals} suspicious deal score(s)`,
        detail: 'These offers have deal scores far outside the normal range (>50% or <-30%). Usually caused by unit parsing errors creating wrong €/kg values.',
        action: 'Review suspicious offers',
        link: '/offers',
      })
    }
  }

  // Sort by severity (highest first), then alphabetically
  items.sort((a, b) => {
    const sevDiff = (SEVERITY[b.severity] ?? 0) - (SEVERITY[a.severity] ?? 0)
    if (sevDiff !== 0) return sevDiff
    return a.title.localeCompare(b.title)
  })

  return items
}

/**
 * Derive source health status from a source object.
 * @returns 'error' | 'warning' | 'healthy'
 */
export function getSourceStatus(source) {
  if (source.errors > 0) return 'error'
  if (source.accepted === 0) return 'warning'
  return 'healthy'
}

/**
 * Derive run health status from a run object.
 * @returns 'error' | 'warning' | 'healthy'
 */
export function getRunStatus(run) {
  if (run.status === 'failed') return 'error'
  if (run.status === 'partial') return 'warning'
  return 'healthy'
}

/**
 * Derive classification health status from agreement rate.
 * @param {number} agreementRate - percentage 0-100
 * @returns 'error' | 'warning' | 'healthy'
 */
export function getClassificationStatus(agreementRate) {
  if (agreementRate < 70) return 'error'
  if (agreementRate < 90) return 'warning'
  return 'healthy'
}

/**
 * Count sources by status.
 * @param {Array} sources
 * @returns {{ healthy: number, warning: number, error: number }}
 */
export function countSourcesByStatus(sources) {
  const counts = { healthy: 0, warning: 0, error: 0 }
  for (const s of sources) {
    counts[getSourceStatus(s)]++
  }
  return counts
}

/**
 * Explain what a classification disagreement means in plain language.
 * Helps the admin understand whether the LLM or the fallback is right.
 *
 * @param {object} sample - A disagreement sample from the API
 * @returns {{ summary: string, explanation: string, likelyCorrect: 'cache'|'fallback'|'unclear' }}
 */
export function explainDisagreement(sample) {
  const tier = sample.tier
  const cacheResult = formatClassification(sample.is_bio, sample.category)
  const fallbackResult = formatClassification(sample.fallback_is_bio, sample.fallback_category)

  // Common case: cache says "not bio" but fallback says "bio"
  // This usually means the LLM correctly rejected a non-produce item
  // that happens to have "bio" in its name (e.g. "AH Biologisch Roeryoghurt")
  if (!sample.is_bio && sample.fallback_is_bio) {
    return {
      summary: `${tier} rejected as non-bio, keywords say bio`,
      explanation: 'The AI says this isn\'t organic produce despite containing a bio keyword. Usually correct for processed foods (yoghurt, juice, snacks).',
      likelyCorrect: 'cache',
    }
  }

  // Cache says bio with a category, fallback disagrees on category
  if (sample.is_bio && sample.fallback_is_bio && sample.category !== sample.fallback_category) {
    return {
      summary: `Both say bio, but disagree on category: ${sample.category ?? 'none'} vs ${sample.fallback_category ?? 'none'}`,
      explanation: 'The product is organic, but the AI and keywords disagree whether it\'s fruit, vegetable, or neither.',
      likelyCorrect: 'unclear',
    }
  }

  return {
    summary: `${tier}: ${cacheResult} vs fallback: ${fallbackResult}`,
    explanation: 'Review the product name and decide which classification is correct.',
    likelyCorrect: 'unclear',
  }
}

function formatClassification(isBio, category) {
  if (!isBio) return 'not bio'
  return category ? `bio → ${category}` : 'bio (no category)'
}

function capitalize(str) {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function formatDateSafe(iso) {
  if (!iso) return '?'
  try {
    return new Date(iso).toLocaleDateString('nl-NL', {
      day: '2-digit', month: '2-digit',
    })
  } catch {
    return iso
  }
}
